import { POST_ADM } from '../../../../services/api'

/**
 * Interface para request da análise de IA
 */
interface AnalyseConciergeRequest {
  prompt: string
  metadata: {
    userId: string
    sessionId: string
    context: string
    webapp: string
    filters: Array<{
      pluginspaceId?: string
      webapp?: string
    }>
  }
  humanized: boolean
}

/**
 * Interface para resposta da análise de IA
 */
export interface AIAnalysisResponse {
  success: boolean
  suggestion: string
  sentiment: 'positive' | 'neutral' | 'negative'
  confidence: number
  metadata?: {
    processingTime?: number
    model?: string
    sqlQuery?: string
    rawData?: any
  }
}

/**
 * Serviço para análise de mensagens pela IA (CAS)
 * Integra com o serviço de Concierge IA para gerar respostas automáticas e sugestões
 */
export class AIAnalysisService {
  /**
   * Solicita análise e sugestão de resposta para uma mensagem do hóspede
   * 
   * @param prompt - Mensagem/pergunta do hóspede
   * @param sessionId - ID da sessão/conversa
   * @param hotelId - ID do hotel (pluginspaceId)
   * @param userId - ID do usuário admin (opcional)
   * @returns Resposta da IA com sugestão e análise de sentimento
   */
  static async analyzeConcierge(
    prompt: string,
    sessionId: string,
    hotelId: number,
    userId?: string
  ): Promise<AIAnalysisResponse> {
    try {
      const request: AnalyseConciergeRequest = {
        prompt,
        metadata: {
          userId: userId || 'admin-user',
          sessionId,
          context: 'hotel_reservation',
          webapp: process.env.REACT_APP_WEBAPP_URL || 'https://hospitality-os.ninegrid.com.br',
          filters: [
            { pluginspaceId: hotelId.toString() },
            { webapp: process.env.REACT_APP_WEBAPP_URL || 'https://hospitality-os.ninegrid.com.br' }
          ]
        },
        humanized: true
      }

      console.log('[AIAnalysis] Enviando para análise:', {
        prompt: prompt.substring(0, 100) + '...',
        sessionId,
        hotelId
      })

      const response = await POST_ADM('/concierge-ia/analyseConcierge', request)

      console.log('[AIAnalysis] Resposta recebida:', {
        success: response.success,
        sentiment: response.sentiment,
        confidence: response.confidence,
        suggestionLength: response.suggestion?.length || 0
      })

      return response as AIAnalysisResponse
    } catch (error: any) {
      console.error('[AIAnalysis] Erro ao analisar mensagem:', error)
      
      // Retornar resposta de fallback em caso de erro
      return {
        success: false,
        suggestion: 'Desculpe, não foi possível gerar uma sugestão no momento. Por favor, responda manualmente.',
        sentiment: 'neutral',
        confidence: 0,
        metadata: {
          processingTime: 0,
          model: 'fallback'
        }
      }
    }
  }

  /**
   * Valida se o serviço de IA está disponível
   * Útil para verificar a saúde do serviço antes de fazer chamadas
   */
  static async checkAvailability(): Promise<boolean> {
    try {
      // Você pode criar uma rota /concierge-ia/health no backend
      const response = await POST_ADM('/concierge-ia/health', {}) as { available: boolean }
      return response.available
    } catch (error) {
      console.warn('[AIAnalysis] Serviço de IA não disponível:', error)
      return false
    }
  }

  /**
   * Analisa sentimento de uma mensagem sem gerar sugestão de resposta
   * Útil para modo manual com análise passiva
   */
  static async analyzeSentiment(
    text: string,
    sessionId: string
  ): Promise<{ sentiment: 'positive' | 'neutral' | 'negative'; confidence: number }> {
    try {
      // Chamar rota específica de sentimento ou usar a principal com flag
      const response = await this.analyzeConcierge(text, sessionId, 0)
      return {
        sentiment: response.sentiment,
        confidence: response.confidence
      }
    } catch (error) {
      console.error('[AIAnalysis] Erro ao analisar sentimento:', error)
      return {
        sentiment: 'neutral',
        confidence: 0
      }
    }
  }
}
